// @flow
import React, {useContext, useEffect, useState} from "react"
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import ArchiverClientApi from "../API/archiverClientApi";
import {useHistory, useLocation} from "react-router-dom";
import MagCard from "./MagCard";
import BackNextWidget from "../GUI_COMMON/COMPONENTS/BackNextWidget";
import SampleSearchInput from "./SampleSearchInput";
import SearchButtonList from "./SearchButtonList";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import {saveSearchResults} from "../ACTIONS/clientActions";

const DEFAULT_MAG_PAGE_SIZE=20;

const SearchMagsPage = () => {

    const [globalState, dispatch] = useContext(StoreContext);

    const [isSearching,setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [term,setTerm] = useState(params.get('s')??"");
    const [isIniting, setIsIniting] = useState(true);
    const [searchPressed,setSearchPressed] = useState(false);
    
    const [pageSize,] = useState(DEFAULT_MAG_PAGE_SIZE);
    const [startIndex, setStartIndex]=useState(Number(params.get('si')??0));
    const [magType, setMagType] = useState(params.get('t')??"");
    
    useEffect(() => {
        // RECALL -- this runs AFTER all children mount!
        console.debug("SearchMagsPage starting up...");
        window.scrollTo(0,0);
        LIUtils.setBackgroundColor('--li-main-bg-color');

        setIsIniting(false);
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect( () => {
        if(!isIniting) {
            setSearchPressed(false); // reset
            if(searchResultsHaveNotChanged()) {
                setSearchResults(globalState.searchResults.searchResults);
            }
            else { // go to the API
                handleBackNext(startIndex, pageSize);
            }
        }
    }, [term,magType, isIniting, searchPressed]); // eslint-disable-line react-hooks/exhaustive-deps

    function buildSearchTypeKey():string {
        return "mag_search_" + magType;
    }
    
    function searchResultsHaveNotChanged() {
        const searchTypeKey = buildSearchTypeKey();
        const gSearchResults = globalState.searchResults;

        return gSearchResults.searchType === searchTypeKey &&
            gSearchResults.startIndex === startIndex &&
            gSearchResults.numToReturn === pageSize &&
            gSearchResults.searchResults !== undefined &&
            gSearchResults.searchTerm === term && 
            !searchPressed;
    }
    
    function handleMagSearchClicked(newTerm) {
        setStartIndex(0);
        setSearchPressed(true);
        if(newTerm===term) {
            handleBackNext(startIndex, pageSize); // force refresh if same term (useEffect won't fire)
        }
        setTerm(newTerm);
    }

    function handleMagClicked(magItem) {
        replaceBrowserHistory(term,startIndex,DEFAULT_MAG_PAGE_SIZE,magType);
        history.push(`/mag_details/${magItem.barcode}`);
    }

    function handleDeregisterMag(magItem) {
        ArchiverClientApi.sendRegisterMagToDevice(magItem.barcode, 0)
        .then((magInfo) => {
            const origSearchResults = [...searchResults];
            const magItem = {...magInfo};
            const origIndex = searchResults.findIndex((item) => item.barcode === magItem.barcode);
            if(origIndex>=0) {
                origSearchResults[origIndex] = magItem;
                setSearchResults(origSearchResults);
            }
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally(() => {});
    }
    
    function handleBackNext(si, numToReturn) {

        console.debug(`*** handling backnext (${si}:${numToReturn}) ***`);
        setStartIndex(si);
        replaceBrowserHistory(term,si,numToReturn,magType);
        if(!term || term==="") {
            return null;
        }
        setIsSearching(true);
        setSearchResults([]);
        ArchiverClientApi.sendMagSearchRequest(term,magType,si, numToReturn)
        .then( (results) => {
            setSearchResults(results);
            dispatch(saveSearchResults(buildSearchTypeKey(),results,si, numToReturn, term)); // save for possible later use
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => setIsSearching(false));
    }

    function replaceBrowserHistory(searchTerm,startIndex,numToReturn,magType) {
        history.replace(`/mag_search?s=${searchTerm}&t=${magType}&si=${startIndex}&nr=${numToReturn}`);
    }
    
    function handleFilterChanged(e) {
        e.stopPropagation();
        setMagType(e.target.value);
        setStartIndex(0);  // reset
    }
    
    function showResultsTable() {
        if(!term || term==="") {
            return <div className="text-center mt-5"/>;
        }
        if(isSearching) {
            return <div className="text-center mt-5">...searching...</div>;
        }
        if(!searchResults || searchResults.length===0) {
            return <div className="badge-pill mt-5 p-3 li-bg-card">no matches found</div>;
        }
        if(searchResults.length%2!==0) { // need an extra one to keep the last left sample to the left (hack)
            searchResults.push(null); // final one
        }

        const resultList = searchResults.map( (magItem, index) => {
                const magType = magItem?.magazineType??null;
                if (magType) { // augment with the number of rows and columns and sample type info
                    const magTypeDetails = globalState.magTypes.items.find((item) => item.id === magType);
                    magItem.rows = magTypeDetails?.numRows??0;
                    magItem.columns = magTypeDetails?.numColumns??0;
                }
                return <MagCard key={index} info={magItem} onClick={() => handleMagClicked(magItem)}
                                onRemoveCurrentOrNext={()=> handleDeregisterMag(magItem)}/>;
            }
        );

        return (
            <div className="row justify-content-center">
                {resultList}
            </div>
        );
    }

    function haveResults() {
        return !isSearching && ((searchResults && searchResults.length>0) || startIndex>0);
    }

    function showMagTypeFilter() {
        return (
            <div className="my-2 my-sm-0">
                <select id="magTypeSelect" className="li-form-field no-width li-border-dark mr-3" name="sample-type-select" aria-label="Mag Type"
                        value={magType} onChange={(e)=>handleFilterChanged(e)}>
                    <option value="">all</option>
                    {globalState.magTypes.items?.map( (item,index) => <option key={index} value={item.id}>{item.description}</option>)}
                </select>
            </div>
        );
    }
    
    return (
        <div className="container">
            <Header title={"Search Magazines - "+ term}/>
            <div className="row li-header-row m-0 p-0 justify-content-between">
                <div className="align-self-center">
                    <BackButton onClick={() => history.push("/dashboard")}/>
                    <h1 className="d-inline-block align-middle">Search</h1>
                </div>
                <SearchButtonList activeChoice="magazines"/>
            </div>
                
            <div className="row pt-4 pb-3 pl-0 justify-content-center align-items-center align-self-center">
                <SampleSearchInput name="magSearch" label="Magazine:" onSearchClicked={handleMagSearchClicked} 
                                   busyFlag={isSearching} 
                                   initialValue={term} medium placeholder="" hasFocus/>
                {showMagTypeFilter()}
            </div>

            <div className="mt-3 mb-4">
                <BackNextWidget disableNext={searchResults.length < pageSize} pageSize={pageSize} startIndex={startIndex} 
                                onClick={handleBackNext} show={haveResults()}/>
                <div className="li-search-results-table mt-2">
                    {showResultsTable()}
                </div>
            </div>
            
            <BackNextWidget disableNext={searchResults.length < pageSize} pageSize={pageSize} startIndex={startIndex} 
                            onClick={handleBackNext} show={haveResults()}/>
        </div>
    );
}

export default SearchMagsPage;